import type { BrandingTokens } from '@dvgup/contracts/branding-config';
import type { ThemeOptions } from '@mui/material/styles';

/**
 * Project BrandingConfig v1 tokens into MUI's `ThemeOptions` shape.
 *
 * Per public-website design §3.2, this is the single seam between the
 * platform's branding contract (`BrandingTokens` from
 * `@dvgup/contracts/branding-config`) and MUI's theme system. The
 * function is pure — same input always produces the same output, with
 * no side effects.
 *
 * Mapping:
 * - `colors.primary` → `palette.primary.main`
 * - `colors.secondary` → `palette.secondary.main`
 * - `colors.accent` → `palette.info.main` (closest semantic MUI slot)
 * - `colors.background` → `palette.background.default`
 * - `colors.text` → `palette.text.primary`
 * - `typography.bodyFont` → `typography.fontFamily`
 * - `typography.headingFont` → headings (`h1` … `h6`) `fontFamily`
 * - `typography.monoFont` → MUI `fontFamilyMonospace` via `Typography` overrides
 *
 * Light + dark color schemes per design §3.2: until TODO-013 (brand
 * assets) lands a richer `BrandingTokens.theme.{light, dark}` shape,
 * the projection produces a single light scheme from the flat color
 * tokens. The composition seam in `theme/index.ts` (task 4.1) layers
 * the LinkBehavior component defaults on top of these options.
 *
 * TODO(BrandingTokens v2 — dark-mode wiring): once `core-infrastructure`
 * extends `BrandingTokens` with a per-mode shape (e.g. `colors.light`
 * + `colors.dark` or a flat `theme.{light,dark}` bundle pulled from a
 * Material Theme Builder export), this projector should switch to MUI
 * 6+'s `colorSchemes: { light: { palette: ... }, dark: { palette: ... } }`
 * shape and emit BOTH schemes. The workspace pins `@mui/material@^9.0.0`
 * which supports CSS-variables-driven `colorSchemes`. Keeping the
 * single-scheme projection for now so the v1 contract surface stays
 * intact; the swap is a tokens-only change at the call sites in
 * `theme/index.ts` plus `tst/theme/index.test.ts`.
 *
 * @param branding The BrandingConfig v1 token bundle.
 * @returns MUI ThemeOptions consumable by `createTheme`.
 */
export function themeOptionsFromBranding(branding: BrandingTokens): ThemeOptions {
  return {
    palette: {
      mode: 'light',
      primary: { main: branding.colors.primary },
      secondary: { main: branding.colors.secondary },
      info: { main: branding.colors.accent },
      background: { default: branding.colors.background },
      text: { primary: branding.colors.text },
    },
    typography: {
      fontFamily: branding.typography.bodyFont,
      h1: { fontFamily: branding.typography.headingFont },
      h2: { fontFamily: branding.typography.headingFont },
      h3: { fontFamily: branding.typography.headingFont },
      h4: { fontFamily: branding.typography.headingFont },
      h5: { fontFamily: branding.typography.headingFont },
      h6: { fontFamily: branding.typography.headingFont },
    },
    shape: {
      borderRadius: 8,
    },
    spacing: 8,
  };
}
